import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button, Empty, Grid, Table, Tooltip, Typography, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import OrderDetail from './OrderDetail';
import {
  ORDER_STATUSES,
  getLogisticsStatus,
  formatUsd,
} from '../utils/orderPresentation';
import { PAGE_SIZES } from '../utils/orderQuery';

const status = (value, options, description) => {
  const item = options[value] || { text: '未知状态', tone: 'neutral' };
  return (
    <span className={'order-status ' + item.tone}>
      {description || item.text}
    </span>
  );
};
const OrderTable = ({
  data,
  loading,
  pagination,
  onChange,
  selectedRowKeys,
  onSelectionChange,
  query,
  hasError,
  onImport,
  expandedColumns = false,
  statusView = 'all',
}) => {
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [bodyHeight, setBodyHeight] = useState(320);
  const containerRef = useRef(null);
  const tableRef = useRef(null);
  const returnFocusId = useRef(null);
  const focusFrame = useRef(null);
  const selectedIndex = data.findIndex((order) => order.id === selectedOrderId);
  const selectedOrder = data[selectedIndex] || null;
  const screens = Grid.useBreakpoint();
  const isCompactMobile = screens.md === false && !expandedColumns;
  useEffect(() => {
    setSelectedOrderId(null);
    returnFocusId.current = null;
    cancelAnimationFrame(focusFrame.current);
  }, [data, query]);
  useEffect(() => () => cancelAnimationFrame(focusFrame.current), []);
  const closeDetail = () => {
    returnFocusId.current = selectedOrderId;
    setSelectedOrderId(null);
  };
  const restoreRowFocus = () => {
    const id = returnFocusId.current;
    if (id == null) return;
    const findButton = () =>
      containerRef.current?.querySelector(
        '[data-row-key="' + CSS.escape(String(id)) + '"] .order-number-link',
      );
    const button = findButton();
    const body = containerRef.current?.querySelector(
      '.ant-table-tbody-virtual',
    );
    const rowBounds = button?.getBoundingClientRect();
    const bodyBounds = body?.getBoundingClientRect();
    if (
      !rowBounds ||
      rowBounds.top < bodyBounds?.top ||
      rowBounds.bottom > bodyBounds?.bottom
    ) {
      tableRef.current?.scrollTo({ key: id });
    }
    focusFrame.current = requestAnimationFrame(() => {
      focusFrame.current = requestAnimationFrame(() => {
        findButton()?.focus({ preventScroll: true });
        returnFocusId.current = null;
      });
    });
  };
  useLayoutEffect(() => {
    const container = containerRef.current;
    const workspace = container.closest('.workspace-content');
    const header = container.querySelector('.ant-table-thead');
    const footer = container.querySelector('.ant-pagination');
    let frame;
    const measure = () => {
      const footerStyle = footer && getComputedStyle(footer);
      const footerHeight = footer
        ? footer.getBoundingClientRect().height +
          parseFloat(footerStyle.marginTop) +
          parseFloat(footerStyle.marginBottom)
        : 0;
      const bottomSpace = workspace
        ? parseFloat(getComputedStyle(workspace).paddingBottom)
        : 24;
      const tableTop = container.getBoundingClientRect().top + window.scrollY;
      setBodyHeight(
        Math.max(
          160,
          Math.floor(
            window.innerHeight -
              tableTop -
              (header?.getBoundingClientRect().height || 40) -
              footerHeight -
              bottomSpace -
              2,
          ),
        ),
      );
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    const observer = new ResizeObserver(schedule);
    [workspace, container.parentElement, header, footer]
      .filter(Boolean)
      .forEach((node) => observer.observe(node));
    window.addEventListener('resize', schedule);
    measure();
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', schedule);
      cancelAnimationFrame(frame);
    };
  }, [isCompactMobile, expandedColumns, data.length]);
  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      message.success('订单号已复制');
    } catch {
      message.error('复制失败，请手动选择订单号复制');
    }
  };
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 90,
      sorter: true,
      sortOrder:
        query.sortBy === 'id'
          ? query.sortDirection === 'asc'
            ? 'ascend'
            : 'descend'
          : null,
      render: (value) => <span className="numeric">{value}</span>,
    },
    {
      title: '订单 / 店铺',
      dataIndex: 'orderNo',
      width: 225,
      render: (value, record) => (
        <div className="order-cell">
          <div>
            <Button
              type="link"
              size="small"
              className="order-number-link order-number"
              onClick={() => setSelectedOrderId(record.id)}
            >
              {value || '查看订单 ' + record.id}
            </Button>
            {value && (
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                aria-label={'复制三方订单号 ' + value}
                onClick={() => copy(value)}
              />
            )}
          </div>
          <small>{record.storeName || '未填写店铺'}</small>
          {isCompactMobile && (
            <div className="mobile-order-summary">
              {status(record.status, ORDER_STATUSES, record.statusDesc)}
              <span className="numeric">{formatUsd(record.total)}</span>
            </div>
          )}
          {isCompactMobile &&
            statusView === 'exceptions' &&
            record.failContent && (
              <small className="order-failure">{record.failContent}</small>
            )}
        </div>
      ),
    },
    {
      title: '亚马逊订单号',
      dataIndex: 'platformOrderNo',
      width: 210,
      render: (value) =>
        value ? (
          <Typography.Text
            className="order-number"
            copyable={{ text: value, tooltips: ['复制亚马逊订单号', '已复制'] }}
          >
            {value}
          </Typography.Text>
        ) : (
          <span className="muted">未关联</span>
        ),
    },
    {
      title: '买家号',
      dataIndex: 'customerId',
      width: 120,
      render: (value) => value || '-',
    },
    {
      title: '订单状态',
      dataIndex: 'status',
      width: 110,
      render: (value, record) =>
        status(value, ORDER_STATUSES, record.statusDesc),
    },
    {
      title: '物流状态',
      dataIndex: 'amzStatus',
      width: 105,
      render: (_, record) => {
        const logistics = getLogisticsStatus(record);
        return (
          <span className={'order-status ' + logistics.tone}>
            {logistics.text}
          </span>
        );
      },
    },
    {
      title: '收货信息',
      key: 'receiving',
      width: 190,
      render: (_, record) => (
        <Tooltip
          title={[
            record.receivingPhone,
            record.receivingAddress,
            record.receivingCity,
            record.state,
            record.receivingPostCode,
          ]
            .filter(Boolean)
            .join(' · ')}
        >
          <div className="order-cell">
            <span>{record.receivingName || '-'}</span>
            <small>
              {[record.receivingCity, record.receivingCountry]
                .filter(Boolean)
                .join(' / ') || '未填写地区'}
            </small>
          </div>
        </Tooltip>
      ),
    },
    {
      title: '物流信息',
      key: 'logistics',
      width: 170,
      render: (_, record) => (
        <div className="order-cell">
          <span>{record.carrier || '暂无承运商'}</span>
          <small>
            {record.trackingNumber || record.logisticsType || '暂无物流信息'}
          </small>
        </div>
      ),
    },
    {
      title: '金额 USD',
      dataIndex: 'total',
      width: 105,
      align: 'right',
      render: (value) => <span className="numeric">{formatUsd(value)}</span>,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      width: 155,
      sorter: true,
      sortOrder:
        query.sortBy === 'createTime'
          ? query.sortDirection === 'asc'
            ? 'ascend'
            : 'descend'
          : null,
      render: (value) => (
        <span className="order-created numeric">
          <span>{value?.split(/[T ]/)[0] || '-'}</span>
          <small>{value?.split(/[T ]/)[1]}</small>
        </span>
      ),
    },
    {
      title: '失败原因',
      dataIndex: 'failContent',
      width: 320,
      render: (value) => (
        <Tooltip title={value}>
          <span className="order-failure">{value || '暂无失败原因'}</span>
        </Tooltip>
      ),
    },
  ];
  const columnOrder = [
    'orderNo',
    'status',
    'amzStatus',
    'total',
    'createTime',
    'platformOrderNo',
    'customerId',
    'receiving',
    'logistics',
    'id',
    'failContent',
  ];
  const reviewColumns = [
    'orderNo',
    'status',
    'customerId',
    'receiving',
    'total',
    'createTime',
  ];
  const columnPresets = {
    0: reviewColumns,
    1: reviewColumns,
    2: [
      'orderNo',
      'platformOrderNo',
      'amzStatus',
      'logistics',
      'total',
      'createTime',
    ],
    exceptions: ['orderNo', 'status', 'failContent', 'createTime'],
  };
  const visibleColumns = isCompactMobile
    ? ['orderNo']
    : expandedColumns
      ? columnOrder
      : columnPresets[statusView] || [
          'orderNo',
          'status',
          'amzStatus',
          'total',
          'receiving',
          'createTime',
        ];
  const orderedColumns = visibleColumns.map((key) => {
    const column = columns.find((item) => (item.dataIndex || item.key) === key);
    return isCompactMobile
      ? {
          ...column,
          width: undefined,
          fixed: undefined,
        }
      : column;
  });
  const filtered = Object.keys(query).some(
    (key) => !['pageNum', 'pageSize', 'sortBy', 'sortDirection'].includes(key),
  );
  return (
    <div className="table-container" ref={containerRef}>
      <Table
        ref={tableRef}
        columns={orderedColumns}
        dataSource={data}
        rowKey="id"
        size="small"
        loading={loading}
        virtual
        // A positive minimum lets rc-table fill the available width on mobile.
        scroll={{
          x: isCompactMobile
            ? 1
            : orderedColumns.reduce(
                (width, column) => width + column.width,
                40,
              ),
          y: bodyHeight,
        }}
        rowSelection={{
          columnWidth: isCompactMobile ? 32 : 40,
          selectedRowKeys,
          onChange: onSelectionChange,
          preserveSelectedRowKeys: false,
          getCheckboxProps: () => ({ disabled: loading || hasError }),
        }}
        pagination={{
          ...pagination,
          pageSizeOptions: PAGE_SIZES,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => '共 ' + total + ' 条订单',
        }}
        onChange={onChange}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                hasError
                  ? '订单加载失败，请重试'
                  : filtered
                    ? '没有符合条件的订单，请调整筛选条件'
                    : '还没有订单，从导入第一份订单表开始'
              }
            >
              {!filtered && !hasError && (
                <Button onClick={onImport}>导入订单</Button>
              )}
            </Empty>
          ),
        }}
      />
      <OrderDetail
        visible={Boolean(selectedOrder)}
        order={selectedOrder}
        onClose={closeDetail}
        onAfterClose={restoreRowFocus}
        navigation={{
          position: selectedIndex + 1,
          total: data.length,
          onPrevious: () =>
            setSelectedOrderId(data[selectedIndex - 1]?.id ?? selectedOrderId),
          onNext: () =>
            setSelectedOrderId(data[selectedIndex + 1]?.id ?? selectedOrderId),
        }}
      />
    </div>
  );
};
export default OrderTable;
